import { useFrame } from '@react-three/fiber';
import { useEffect, useRef, type RefObject } from 'react';
import type { Object3D } from 'three';
import { Vector3 } from 'three';

import { usePlayerFollowTarget } from '../hooks/usePlayerFollowTarget';
import { usePlayerCameraControls } from '../hooks/usePlayerCameraControls';
import { usePlayerSettings } from '../hooks/usePlayerSettings';
import { isPlayerCameraUpdateBlocked } from '../lib/isPlayerCameraUpdateBlocked';
import { usePlayerStoreApi } from '../store/playerStore';
import type { PlayerCameraControlsHandle } from '../types';

const TARGET_Y_OFFSET = -0.5;

export interface PlayerFollowCameraProps {
  cameraControlsRef?: RefObject<PlayerCameraControlsHandle | null>;
}

export default function PlayerFollowCamera({
  cameraControlsRef,
}: PlayerFollowCameraProps) {
  const {
    settings: {
      camera: { follow },
    },
  } = usePlayerSettings();
  const store = usePlayerStoreApi();
  const { cameraControls } = usePlayerCameraControls({ cameraControlsRef });
  const { targetObject } = usePlayerFollowTarget();
  const cameraOffsetRef = useRef<Vector3 | null>(null);
  const previousTargetRef = useRef<Object3D | null>(null);

  useEffect(() => {
    if (previousTargetRef.current === targetObject) {
      return;
    }

    previousTargetRef.current = targetObject;
    cameraOffsetRef.current = null;
  }, [targetObject]);

  useEffect(() => {
    if (!follow || !targetObject || !cameraControls) {
      cameraOffsetRef.current = null;
      return;
    }

    const currentPosition = cameraControls.getPosition(new Vector3());
    const targetPosition = targetObject.getWorldPosition(new Vector3());
    targetPosition.y += TARGET_Y_OFFSET;
    cameraOffsetRef.current = currentPosition.sub(targetPosition);
  }, [cameraControls, follow, targetObject]);

  useFrame((_, delta) => {
    if (
      !follow ||
      !targetObject ||
      !cameraControls ||
      isPlayerCameraUpdateBlocked(store)
    ) {
      return;
    }

    const targetPosition = targetObject.getWorldPosition(new Vector3());
    targetPosition.y += TARGET_Y_OFFSET;

    const offset =
      cameraOffsetRef.current ??
      cameraControls.getPosition(new Vector3()).sub(targetPosition.clone());
    cameraOffsetRef.current = offset;

    const desiredPosition = targetPosition.clone().add(offset);
    cameraControls.setLookAt(
      desiredPosition.x,
      desiredPosition.y,
      desiredPosition.z,
      targetPosition.x,
      targetPosition.y,
      targetPosition.z,
      false,
    );
    cameraControls.update(delta);
  });

  return null;
}
